import { api } from "@/lib/api";
import { signEvmMessage } from "@/lib/onchain";

/** Map wallet ecosystem → Utility profile chain path segment. */
export function flipProfileChain(wallet) {
  if (!wallet) return "ethereum";
  return wallet.ecosystem === "solana" ? "solana" : "ethereum";
}

export function flipSupportsSignedMutations(wallet) {
  return Boolean(wallet?.address && wallet.ecosystem !== "solana");
}

export const EVM_ONLY_MUTATION_HINT =
  "Conviction Calls and other signed FLIP actions are currently available with EVM wallets only.";

export function flipBackerPath(wallet) {
  if (!wallet?.address) return "/flip";
  return `/backer/${flipProfileChain(wallet)}/${wallet.address}`;
}

export const getFlipConfig = () =>
  api.get("/flip/config").then((r) => r.data);

export const getFlipTokenomics = () =>
  api.get("/flip/tokenomics").then((r) => r.data);

export const getFlipStatus = (chain, wallet) =>
  api.get(`/flip/status/${encodeURIComponent(chain)}/${encodeURIComponent(wallet)}`).then((r) => r.data);

export const getFlipProfile = (chain, wallet) =>
  api.get(`/flip/profile/${encodeURIComponent(chain)}/${encodeURIComponent(wallet)}`).then((r) => r.data);

export const getFlipProfileAchievements = (chain, wallet) =>
  api
    .get(`/flip/profile/${encodeURIComponent(chain)}/${encodeURIComponent(wallet)}/achievements`)
    .then((r) => r.data);

export const getFlipProfileCalls = (chain, wallet, params = {}) =>
  api
    .get(`/flip/profile/${encodeURIComponent(chain)}/${encodeURIComponent(wallet)}/calls`, { params })
    .then((r) => r.data);

export const getFlipReputation = (chain, wallet) =>
  api
    .get(`/flip/reputation/${encodeURIComponent(chain)}/${encodeURIComponent(wallet)}`)
    .then((r) => r.data);

export const getFlipCalls = (params = {}) =>
  api.get("/flip/calls", { params }).then((r) => r.data);

export const getFlipCall = (id) =>
  api.get(`/flip/calls/${encodeURIComponent(id)}`).then((r) => r.data);

export const getFlipCampaigns = (params = {}) =>
  api.get("/flip/campaigns", { params }).then((r) => r.data);

export const getFlipCampaign = (id) =>
  api.get(`/flip/campaigns/${encodeURIComponent(id)}`).then((r) => r.data);

export const getFlipWatchlist = () =>
  api.get("/flip/watchlist").then((r) => r.data);

export const putFlipWatchlist = (entity_ids) =>
  api.put("/flip/watchlist", { entity_ids }).then((r) => r.data);

export const getFlipEntityAnalytics = (slug) =>
  api.get(`/flip/analytics/entity/${encodeURIComponent(slug)}`).then((r) => r.data);

export const requestFlipNonce = ({ wallet, chain, action }) =>
  api.post("/flip/auth/nonce", { wallet, chain, action }).then((r) => r.data);

export const createFlipSession = ({ wallet, chain, nonce, signature }) =>
  api.post("/flip/auth/session", { wallet, chain, nonce, signature }).then((r) => r.data);

export const getFlipAuthMe = () =>
  api.get("/flip/auth/me").then((r) => r.data);

export const logoutFlipSession = () =>
  api.post("/flip/auth/logout").then((r) => r.data);

/**
 * Bootstrap a short-lived FLIP wallet session (HttpOnly cookie).
 * EVM wallets only in V1.
 */
export async function ensureFlipWalletSession(wallet) {
  if (!flipSupportsSignedMutations(wallet)) {
    throw new Error(EVM_ONLY_MUTATION_HINT);
  }
  const chain = flipProfileChain(wallet);
  const me = await getFlipAuthMe().catch(() => ({ authenticated: false }));
  if (
    me?.authenticated &&
    me.wallet?.toLowerCase() === wallet.address.toLowerCase() &&
    me.chain === chain
  ) {
    return me;
  }
  const issued = await requestFlipNonce({
    wallet: wallet.address,
    chain,
    action: "wallet_session",
  });
  const message = issued.message;
  const { signature } = await signEvmMessage(message);
  return createFlipSession({
    wallet: wallet.address,
    chain,
    nonce: issued.nonce,
    signature,
  });
}

/** Build + sign + submit a Conviction Call using server nonce + canonical message. */
export async function createFlipCall(wallet, fields) {
  if (!flipSupportsSignedMutations(wallet)) {
    throw new Error(EVM_ONLY_MUTATION_HINT);
  }
  const chain = flipProfileChain(wallet);
  const issued = await requestFlipNonce({
    wallet: wallet.address,
    chain,
    action: "call_create",
  });
  const payload = {
    action: "call_create",
    wallet: wallet.address.toLowerCase(),
    chain,
    nonce: issued.nonce,
    call_type: fields.call_type,
    subject_entity_id: fields.subject_entity_id,
    target_entity_id: fields.target_entity_id ?? null,
    target_value: fields.target_value == null ? null : Number(fields.target_value),
    deadline: fields.deadline,
    reputation_at_risk: Number(fields.reputation_at_risk),
  };
  const signed_message = canonicalFlipMessage(payload);
  const { signature } = await signEvmMessage(signed_message);
  return api
    .post("/flip/calls", {
      creator_wallet: wallet.address,
      creator_chain: chain,
      call_type: fields.call_type,
      subject_entity_id: fields.subject_entity_id,
      target_entity_id: fields.target_entity_id ?? null,
      target_value: fields.target_value ?? null,
      deadline: fields.deadline,
      reputation_at_risk: Number(fields.reputation_at_risk),
      nonce: issued.nonce,
      signature,
      signed_message,
    })
    .then((r) => r.data);
}

export async function createFlipCampaign(wallet, fields) {
  if (!flipSupportsSignedMutations(wallet)) {
    throw new Error(EVM_ONLY_MUTATION_HINT);
  }
  const chain = flipProfileChain(wallet);
  const issued = await requestFlipNonce({
    wallet: wallet.address,
    chain,
    action: "campaign_create",
  });
  const payload = {
    action: "campaign_create",
    wallet: wallet.address.toLowerCase(),
    chain,
    nonce: issued.nonce,
    title: fields.title,
    description: fields.description || "",
    entity_id: fields.entity_id,
    target_type: fields.target_type,
    target_value: fields.target_value == null ? null : Number(fields.target_value),
    target_entity_id: fields.target_entity_id ?? null,
    deadline: fields.deadline,
  };
  const signed_message = canonicalFlipMessage(payload);
  const { signature } = await signEvmMessage(signed_message);
  return api
    .post("/flip/campaigns", {
      creator_wallet: wallet.address,
      creator_chain: chain,
      title: fields.title,
      description: fields.description || "",
      entity_id: fields.entity_id,
      target_type: fields.target_type,
      target_value: fields.target_value ?? null,
      target_entity_id: fields.target_entity_id ?? null,
      deadline: fields.deadline,
      nonce: issued.nonce,
      signature,
      signed_message,
    })
    .then((r) => r.data);
}

/** Deterministic message matching backend flip_token.canonical.build_signed_message */
export function canonicalFlipMessage(payload) {
  const body = JSON.stringify(canonicalizeValue(payload));
  return `backonflip.com/flip\nv1\n${body}`;
}

function normalizeNumber(value) {
  if (value === null || value === undefined) return null;
  const d = Number(value);
  if (!Number.isFinite(d)) throw new Error(`invalid number: ${value}`);
  // Match Python Decimal normalize + format(f) without trailing zeros.
  let text = String(d);
  if (text.includes("e") || text.includes("E")) {
    text = d.toFixed(18).replace(/\.?0+$/, "");
  }
  if (text.includes(".")) {
    text = text.replace(/0+$/, "").replace(/\.$/, "");
  }
  return text || "0";
}

function canonicalizeValue(value) {
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return normalizeNumber(value);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(canonicalizeValue);
  if (typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value).sort()) {
      out[k] = canonicalizeValue(value[k]);
    }
    return out;
  }
  return value;
}

function sortKeysDeep(value) {
  return canonicalizeValue(value);
}

export const adminGetFlipSettings = () =>
  api.get("/admin/flip/settings").then((r) => r.data);

export const adminPatchFlipSettings = (payload) =>
  api.patch("/admin/flip/settings", payload).then((r) => r.data);

export const adminListFlipRewards = (params = {}) =>
  api.get("/admin/flip/rewards", { params }).then((r) => r.data);

export const adminApproveFlipReward = (id) =>
  api.post(`/admin/flip/rewards/${encodeURIComponent(id)}/approve`).then((r) => r.data);

export const adminRejectFlipReward = (id) =>
  api.post(`/admin/flip/rewards/${encodeURIComponent(id)}/reject`).then((r) => r.data);

export const adminExportFlipRewards = (status = "APPROVED") =>
  api.get("/admin/flip/rewards/export", { params: { status } }).then((r) => r.data);

export const adminRebuildFlipReputation = (wallet, chain) =>
  api
    .post("/admin/flip/reputation/rebuild", null, { params: { wallet, chain } })
    .then((r) => r.data);
